import React, { useState, useEffect, useMemo } from "react";
import { Box, Stack } from "@mui/material";
import { useNavigate } from "react-router";
import Topbar from "../../../../../../component/TopBar";
import MainBox from "../../../../container/MainBox";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import DataTable from "../../../../../../component/DataTable";
import { SearchInput, StatCard, StatusChip } from "../../../../../../component/listing";
import { formatDate, formatDateByDay } from "../../../../../../utils/formatDate";
import { getAllAttendancePolicies } from "../../../../../../services/attendancePolicy";
import { getEmployeeNames } from "../../../../../../utils/getEmployeeNames";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens } from "../../../../../../theme";

const isExpired = (p) => p?.endDate && new Date(p.endDate) < new Date();

function AttendancePolicyListing() {
  const navigate = useNavigate();
  const notify = useNotify();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getAllAttendancePolicies();
      if (res?.success === false) throw new Error(res?.message || "Failed to load attendance policies");
      const employeeIds = res.data?.filter((d) => d.createdBy)?.map((d) => d.createdBy);
      const combinedData = await getEmployeeNames(res.data, employeeIds, "createdBy");
      setData(Array.isArray(combinedData) ? combinedData.reverse() : []);
    } catch (err) {
      setError(true);
      notify.error(err?.message || "Failed to load attendance policies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const stats = useMemo(() => ({
    total: data.length,
    active: data.filter((p) => !isExpired(p)).length,
    expired: data.filter((p) => isExpired(p)).length,
  }), [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((p) =>
      (p.name || "").toLowerCase().includes(q) || (p.code || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  const openEdit = (id) => navigate("/app/attendance-policy/edit", { state: { policyId: id } });

  return (
    <Box>
      <Topbar MainHeading={"Attendance Policy"} />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: "1.5rem" }}>
        <StatCard label="Total policies" value={stats.total} />
        <StatCard label="Active" value={stats.active} accent={tokens.success} />
        <StatCard label="Expired" value={stats.expired} accent={tokens.textMuted} />
      </Stack>

      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: "1.25rem" }}>
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or code" />
          <BtnPrimary to="/app/attendance-policy/new/setting" sx={{ borderRadius: "8px", whiteSpace: "nowrap" }}>
            + Create Policy
          </BtnPrimary>
        </Stack>

        <DataTable
          rows={filtered}
          loading={loading}
          error={error}
          onRetry={fetchData}
          errorTitle="Couldn't load policies"
          empty={{
            title: data.length === 0 ? "No attendance policies yet" : "No matches",
            description:
              data.length === 0
                ? "Create your first policy to define clock-in rules, tardiness and absenteeism."
                : "Try a different search.",
          }}
          onRowClick={(p) => openEdit(p._id)}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={[
            { key: "code", label: "Code", render: (p) => p.code || "—" },
            {
              key: "name",
              label: "Policy Name",
              cellSx: { fontWeight: 600, color: tokens.accent },
              render: (p) => p.name || "—",
            },
            {
              key: "validity",
              label: "Validity",
              cellSx: { color: tokens.textMuted },
              render: (p) => (p.startDate ? `${formatDate(p.startDate)} – ${formatDate(p.endDate)}` : "—"),
            },
            {
              key: "status",
              label: "Status",
              render: (p) =>
                p.status === "draft" ? (
                  <StatusChip label="Draft" tone="warning" />
                ) : (
                  <StatusChip
                    label={isExpired(p) ? "Expired" : "Active"}
                    tone={isExpired(p) ? "muted" : "success"}
                  />
                ),
            },
            {
              key: "createdBy",
              label: "Created By",
              render: (p) =>
                p?.employee ? `${p.employee.fname || ""} ${p.employee.lname || ""}`.trim() : "—",
            },
            {
              key: "updatedAt",
              label: "Last Modified",
              cellSx: { color: tokens.textMuted },
              render: (p) => formatDateByDay(p.updatedAt),
            },
          ]}
        />

      </MainBox>
    </Box>
  );
}

export default AttendancePolicyListing;
